/**
 * The Wheel — bespoke sound palette.
 *
 * Built on `juice/audio.ts` primitives per the BUILDER-PLAYBOOK rule that
 * every game invents its own feel. Pitch jitter ±15 cents, level jitter
 * ±2 dB on every sound. All routed through `playIfNotMuted()`.
 *
 * Spec (per the dispatch brief synthesised from Research B):
 *   - Wheel clack: filtered noise burst @ 1200 Hz bandpass Q=4 + 180 Hz sine
 *     transient, 60-80ms envelope. One clack per 15° rotation. Falls
 *     naturally with spin deceleration.
 *   - Letter reveal chime: additive sine bell 1320 Hz + 1980 Hz, quick
 *     attack + 600ms decay. Pitch jitter ±15 cents.
 *   - Wedge landing fanfare (low value): single warm tone 660 Hz, ~300ms.
 *   - Wedge landing fanfare (high value $2500+): three-note ascending
 *     arpeggio C5 → E5 → G5, ~500ms.
 *   - Bankrupt thud: low filtered noise at 80 Hz with rapid envelope,
 *     ~200ms. Soft, not punishing.
 *   - Solve success: warm 5-note ascending arpeggio, ~1200ms.
 *   - Vowel purchase: gentle two-note descending soft pad, ~400ms.
 */
import {
  connectThrough,
  envelope,
  getAudioContext,
  getMasterGain,
  makeOscillator,
  noiseBuffer,
  playIfNotMuted,
} from '@/juice/audio';

// ---------------------------------------------------------------------------
// Jitter helpers — the polish recipe (±15¢ pitch, ±2 dB level)
// ---------------------------------------------------------------------------

function jitterCents(): number {
  return (Math.random() - 0.5) * 30;
}

function jitterGain(): number {
  return 1 + (Math.random() - 0.5) * 0.25;
}

// ---------------------------------------------------------------------------
// Wheel clack — one per 15° rotation
// ---------------------------------------------------------------------------

function synthesizeClack(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;

  // Bandpass-filtered noise burst at 1200 Hz, Q=4.
  const noiseBuf = noiseBuffer(ctx, { lengthMs: 80, shaping: 1.5 });
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = noiseBuf;
  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.setValueAtTime(1200 * (1 + jitterCents() / 1200), t);
  bandpass.Q.setValueAtTime(4, t);

  const noiseGain = ctx.createGain();
  const peakNoise = 0.18 * jitterGain();
  noiseGain.gain.setValueAtTime(peakNoise, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);

  connectThrough(noiseSource, bandpass, noiseGain, master);
  noiseSource.start(t);
  noiseSource.stop(t + 0.08);

  // Woody sine transient at 180 Hz, 40ms decay.
  const woody = makeOscillator(ctx, { freq: 180, type: 'sine', detune: jitterCents() });
  const { node: env, tEnd } = envelope(ctx, {
    attack: 0.001,
    decay: 0.02,
    sustain: 0.05,
    release: 0.04,
    peak: 0.22 * jitterGain(),
  });
  connectThrough(woody, env, master);
  woody.start(t);
  woody.stop(tEnd + 0.01);
}

// ---------------------------------------------------------------------------
// Letter reveal chime — additive sine bell, pitch jitter ±15¢
// ---------------------------------------------------------------------------

function synthesizeChime(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  // Fundamental at ~1320 Hz (E6) + 5th harmonic at ~1980 Hz.
  const fundamental = makeOscillator(ctx, { freq: 1320, type: 'sine', detune: jitterCents() });
  const harmonic = makeOscillator(ctx, { freq: 1980, type: 'sine', detune: jitterCents() });

  const fundEnv = envelope(ctx, {
    attack: 0.005,
    decay: 0.08,
    sustain: 0.18,
    release: 0.55,
    peak: 0.22 * jitterGain(),
  });
  const harmEnv = envelope(ctx, {
    attack: 0.005,
    decay: 0.08,
    sustain: 0.1,
    release: 0.45,
    peak: 0.12 * jitterGain(),
  });

  connectThrough(fundamental, fundEnv.node, master);
  connectThrough(harmonic, harmEnv.node, master);

  fundamental.start(t);
  fundamental.stop(fundEnv.tEnd + 0.02);
  harmonic.start(t);
  harmonic.stop(harmEnv.tEnd + 0.02);
}

// ---------------------------------------------------------------------------
// Low-value cash fanfare — single warm tone @ 660 Hz, ~300ms
// ---------------------------------------------------------------------------

function synthesizeLandLow(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const osc = makeOscillator(ctx, { freq: 660, type: 'sine', detune: jitterCents() });
  const { node: env, tEnd } = envelope(ctx, {
    attack: 0.015,
    decay: 0.08,
    sustain: 0.4,
    release: 0.2,
    peak: 0.22 * jitterGain(),
  });
  connectThrough(osc, env, master);
  osc.start(t);
  osc.stop(tEnd + 0.02);
}

// ---------------------------------------------------------------------------
// High-value cash fanfare — C5 → E5 → G5 ascending arpeggio, ~500ms
// ---------------------------------------------------------------------------

function synthesizeLandHigh(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
  for (let i = 0; i < notes.length; i += 1) {
    const osc = makeOscillator(ctx, {
      freq: notes[i]!,
      type: 'sine',
      detune: jitterCents(),
    });
    const { node: env, tEnd } = envelope(ctx, {
      attack: 0.008,
      decay: 0.08,
      sustain: 0.25,
      release: 0.18,
      peak: 0.2 * jitterGain(),
    });
    connectThrough(osc, env, master);
    osc.start(t + i * 0.08);
    osc.stop(tEnd + 0.02);
  }
}

// ---------------------------------------------------------------------------
// Bankrupt — low filtered noise thud, ~200ms, NOT punishing
// ---------------------------------------------------------------------------

function synthesizeBankrupt(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const noiseBuf = noiseBuffer(ctx, { lengthMs: 200, shaping: 2 });
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(180, t);
  lp.Q.setValueAtTime(0.7, t);

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.0001, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.32 * jitterGain(), t + 0.015);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);

  // Add a low sine to ground it.
  const sub = makeOscillator(ctx, { freq: 80, type: 'sine', detune: jitterCents() });
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.0001, t);
  subGain.gain.exponentialRampToValueAtTime(0.18 * jitterGain(), t + 0.02);
  subGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);

  connectThrough(src, lp, noiseGain, master);
  connectThrough(sub, subGain, master);
  src.start(t);
  src.stop(t + 0.28);
  sub.start(t);
  sub.stop(t + 0.28);
}

// ---------------------------------------------------------------------------
// Lose-a-Turn — gentle descending two-note "aw", ~400ms (NOT a buzzer)
// ---------------------------------------------------------------------------

function synthesizeLoseTurn(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const notes = [392, 329.63]; // G4 → E4 (gentle descend)
  for (let i = 0; i < notes.length; i += 1) {
    const osc = makeOscillator(ctx, {
      freq: notes[i]!,
      type: 'sine',
      detune: jitterCents(),
    });
    const { node: env, tEnd } = envelope(ctx, {
      attack: 0.015,
      decay: 0.05,
      sustain: 0.4,
      release: 0.18,
      peak: 0.14 * jitterGain(),
    });
    connectThrough(osc, env, master);
    osc.start(t + i * 0.16);
    osc.stop(tEnd + 0.02);
  }
}

// ---------------------------------------------------------------------------
// Wrong consonant — soft "not in the puzzle" tone (gentle, NOT a buzzer)
// ---------------------------------------------------------------------------

function synthesizeWrongLetter(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const osc = makeOscillator(ctx, { freq: 330, type: 'triangle', detune: jitterCents() });
  const { node: env, tEnd } = envelope(ctx, {
    attack: 0.015,
    decay: 0.06,
    sustain: 0.4,
    release: 0.2,
    peak: 0.14 * jitterGain(),
  });
  osc.frequency.exponentialRampToValueAtTime(247, t + 0.35);
  connectThrough(osc, env, master);
  osc.start(t);
  osc.stop(tEnd + 0.02);
}

// ---------------------------------------------------------------------------
// Free Play — bright two-note rise (a kind little token)
// ---------------------------------------------------------------------------

function synthesizeFreePlay(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const notes = [659.25, 987.77]; // E5 → B5
  for (let i = 0; i < notes.length; i += 1) {
    const osc = makeOscillator(ctx, {
      freq: notes[i]!,
      type: 'sine',
      detune: jitterCents(),
    });
    const { node: env, tEnd } = envelope(ctx, {
      attack: 0.008,
      decay: 0.06,
      sustain: 0.3,
      release: 0.2,
      peak: 0.16 * jitterGain(),
    });
    connectThrough(osc, env, master);
    osc.start(t + i * 0.08);
    osc.stop(tEnd + 0.02);
  }
}

// ---------------------------------------------------------------------------
// Solve success — warm 5-note ascending arpeggio, ~1200ms
// ---------------------------------------------------------------------------

function synthesizeSolveSuccess(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  // C major arpeggio across two octaves: C4 E4 G4 C5 E5
  const notes = [261.63, 329.63, 392.0, 523.25, 659.25];
  for (let i = 0; i < notes.length; i += 1) {
    const osc = makeOscillator(ctx, {
      freq: notes[i]!,
      type: 'sine',
      detune: jitterCents(),
    });
    const { node: env, tEnd } = envelope(ctx, {
      attack: 0.018,
      decay: 0.18,
      sustain: 0.45,
      release: 0.45,
      peak: 0.2 * jitterGain(),
    });
    connectThrough(osc, env, master);
    osc.start(t + i * 0.16);
    osc.stop(tEnd + 0.04);
  }
}

// ---------------------------------------------------------------------------
// Solve miss — same gentle fall as lose-a-turn but quieter
// ---------------------------------------------------------------------------

function synthesizeSolveMiss(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const osc = makeOscillator(ctx, { freq: 392, type: 'sine', detune: jitterCents() });
  const { node: env, tEnd } = envelope(ctx, {
    attack: 0.02,
    decay: 0.12,
    sustain: 0.3,
    release: 0.22,
    peak: 0.12 * jitterGain(),
  });
  osc.frequency.exponentialRampToValueAtTime(294, t + 0.4);
  connectThrough(osc, env, master);
  osc.start(t);
  osc.stop(tEnd + 0.02);
}

// ---------------------------------------------------------------------------
// Vowel purchase — gentle two-note descending soft pad, ~400ms
// ---------------------------------------------------------------------------

function synthesizeVowelPurchase(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const notes = [587.33, 440.0]; // D5 → A4
  for (let i = 0; i < notes.length; i += 1) {
    const osc = makeOscillator(ctx, {
      freq: notes[i]!,
      type: 'sine',
      detune: jitterCents(),
    });
    const { node: env, tEnd } = envelope(ctx, {
      attack: 0.018,
      decay: 0.08,
      sustain: 0.4,
      release: 0.18,
      peak: 0.16 * jitterGain(),
    });
    connectThrough(osc, env, master);
    osc.start(t + i * 0.18);
    osc.stop(tEnd + 0.02);
  }
}

// ---------------------------------------------------------------------------
// Key tap — gentle confirmation when a keyboard letter is pressed
// ---------------------------------------------------------------------------

function synthesizeKeyTap(ctx: AudioContext, master: GainNode): void {
  const t = ctx.currentTime;
  const osc = makeOscillator(ctx, { freq: 880, type: 'triangle', detune: jitterCents() });
  const { node: env, tEnd } = envelope(ctx, {
    attack: 0.003,
    release: 0.05,
    peak: 0.1 * jitterGain(),
  });
  connectThrough(osc, env, master);
  osc.start(t);
  osc.stop(tEnd + 0.01);
}

// ---------------------------------------------------------------------------
// Public palette
// ---------------------------------------------------------------------------

export const WheelSounds = {
  clack(): void {
    playIfNotMuted('wheel-clack', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeClack(ctx, master);
    });
  },

  chime(): void {
    playIfNotMuted('wheel-chime', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeChime(ctx, master);
    });
  },

  landLow(): void {
    playIfNotMuted('wheel-land-low', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeLandLow(ctx, master);
    });
  },

  landHigh(): void {
    playIfNotMuted('wheel-land-high', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeLandHigh(ctx, master);
    });
  },

  bankrupt(): void {
    playIfNotMuted('wheel-bankrupt', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeBankrupt(ctx, master);
    });
  },

  loseTurn(): void {
    playIfNotMuted('wheel-lose-turn', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeLoseTurn(ctx, master);
    });
  },

  wrongLetter(): void {
    playIfNotMuted('wheel-wrong-letter', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeWrongLetter(ctx, master);
    });
  },

  freePlay(): void {
    playIfNotMuted('wheel-free-play', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeFreePlay(ctx, master);
    });
  },

  solveSuccess(): void {
    playIfNotMuted('wheel-solve-success', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeSolveSuccess(ctx, master);
    });
  },

  solveMiss(): void {
    playIfNotMuted('wheel-solve-miss', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeSolveMiss(ctx, master);
    });
  },

  vowelPurchase(): void {
    playIfNotMuted('wheel-vowel', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeVowelPurchase(ctx, master);
    });
  },

  keyTap(): void {
    playIfNotMuted('wheel-key-tap', () => {
      const ctx = getAudioContext();
      const master = getMasterGain();
      if (ctx && master) synthesizeKeyTap(ctx, master);
    });
  },
} as const;
